import * as z from 'zod';

export const CreateCheckoutTemplateRequest = z.object({
  checkoutTemplate: z
    .object({
      _id: z
        .string()
        .describe('Checkout template ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
      customization: z
        .object({
          webClient: z
            .object({
              disabledContinueShoppingButton: z
                .boolean()
                .describe(
                  'Whether to disable the "Continue shopping" button.\n\nDefault: `false`'
                )
                .optional(),
              disabledPolicyAgreementCheckbox: z
                .boolean()
                .describe(
                  'Whether to disable policy agreement checkout in the checkout page\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              'Web client customizations. These customizations only apply to the standard Wix checkout page.'
            )
            .optional(),
          lockedGiftCard: z
            .boolean()
            .describe(
              'Whether to lock the ability to add or remove a gift card.\n\nDefault: `false`'
            )
            .optional(),
          lockedCoupon: z
            .boolean()
            .describe(
              'Whether to lock the ability to apply or remove a coupon code.\n\nDefault: `false`'
            )
            .optional(),
          disabledManualPayment: z
            .boolean()
            .describe(
              'Whether to disable manual payment option for this checkout.\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe(
          'Custom settings to apply to the checkout page created from this template.'
        )
        .optional(),
      lineItems: z
        .array(
          z.object({
            quantity: z
              .number()
              .int()
              .describe('Item quantity.\n\nMin: `1` <br />\nMax: `100000`')
              .min(1)
              .max(100000),
            catalogReference: z
              .object({
                catalogItemId: z
                  .string()
                  .describe('ID of the item within the catalog it belongs to.')
                  .min(1)
                  .max(36),
                appId: z
                  .string()
                  .describe(
                    'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                  )
                  .min(1),
                options: z
                  .record(z.string(), z.any())
                  .describe(
                    'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Catalog and item reference. Holds IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
              ),
            catalogOverrideFields: z
              .object({
                productName: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                      )
                      .min(1)
                      .max(200)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                      )
                      .min(1)
                      .max(400)
                      .optional()
                      .nullable(),
                  })
                  .describe('Item name.')
                  .optional(),
                price: z
                  .string()
                  .describe('Item price **after** discounts.')
                  .optional()
                  .nullable(),
                fullPrice: z
                  .string()
                  .describe('Item price **before** discounts.')
                  .optional()
                  .nullable(),
                descriptionLines: z
                  .array(
                    z.intersection(
                      z.object({
                        name: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                              )
                              .max(100)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                          })
                          .describe('Description line name.')
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          plainText: z.never().optional(),
                          colorInfo: z.never().optional(),
                        }),
                        z.object({
                          colorInfo: z.never().optional(),
                          plainText: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                )
                                .max(600)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                                )
                                .max(600)
                                .optional()
                                .nullable(),
                            })
                            .describe('Description line plain text value.'),
                        }),
                        z.object({
                          plainText: z.never().optional(),
                          colorInfo: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                )
                                .max(500)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                                )
                                .max(500)
                                .optional()
                                .nullable(),
                              code: z
                                .string()
                                .describe('HEX or RGB color code for display.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Description line color value.'),
                        }),
                      ])
                    )
                  )
                  .min(0)
                  .max(20)
                  .optional(),
                physicalProperties: z
                  .object({
                    weight: z
                      .number()
                      .describe(
                        'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                      )
                      .optional()
                      .nullable(),
                    sku: z
                      .string()
                      .describe(
                        'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                      )
                      .max(40)
                      .optional()
                      .nullable(),
                    shippable: z
                      .boolean()
                      .describe('Whether this line item is shippable.')
                      .optional(),
                  })
                  .describe('Physical properties of the item.')
                  .optional(),
                image: z.string().describe('Item image.').optional(),
                paymentOption: z
                  .object({
                    value: z
                      .enum([
                        'FULL_PAYMENT_ONLINE',
                        'FULL_PAYMENT_OFFLINE',
                        'MEMBERSHIP',
                        'DEPOSIT_ONLINE',
                        'MEMBERSHIP_OFFLINE',
                        'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                      ])
                      .optional(),
                  })
                  .describe('Payment method selected for the item.')
                  .optional(),
                depositAmount: z
                  .string()
                  .describe(
                    'Only eligible for catalog items with `lineItem.paymentOption.value` type of `DEPOSIT_ONLINE`.'
                  )
                  .optional()
                  .nullable(),
                savePaymentMethod: z
                  .boolean()
                  .describe(
                    'Whether to save the payment method on the order.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                inventoryAppId: z
                  .string()
                  .describe('ID of the app managing the inventory.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Overriding values for catalog item properties.')
              .optional(),
          })
        )
        .max(300)
        .optional(),
      customLineItems: z
        .array(
          z.object({
            quantity: z
              .number()
              .int()
              .describe('Custom line item quantity.\n\nMin: `1`\nMax: `100000`')
              .min(1)
              .max(100000),
            price: z
              .string()
              .describe(
                'Custom line item price.\n\nMust be a number or a decimal without symbols.'
              )
              .optional(),
            priceDescription: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                  )
                  .min(1)
                  .max(100)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
              )
              .optional(),
            descriptionLines: z
              .array(
                z.intersection(
                  z.object({
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                          )
                          .max(100)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                      .describe('Description line name.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z.never().optional(),
                    }),
                    z.object({
                      colorInfo: z.never().optional(),
                      plainText: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(600)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(600)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line plain text value.'),
                    }),
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(500)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          code: z
                            .string()
                            .describe('HEX or RGB color code for display.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line color value.'),
                    }),
                  ])
                )
              )
              .max(10)
              .optional(),
            media: z.string().describe("Custom line item's media.").optional(),
            _id: z
              .string()
              .describe(
                'Deprecated. Custom line item ID.\n\nTo maintain a unique value for a custom line item across multiple carts and orders, pass `catalogReference` instead.\nLearn more about [custom items with catalog reference](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/catalogs/catalog-and-custom-items#custom-item-with-catalog-reference).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe('Tax group ID for this custom line item.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            productName: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe('Name of the item or product.'),
            url: z
              .string()
              .describe(
                "URL to the item's page on the site. When not provided, the link back from the cart page to the relevant product page will not work."
              )
              .optional(),
            itemType: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    preset: z.never().optional(),
                    custom: z.never().optional(),
                  }),
                  z.object({
                    custom: z.never().optional(),
                    preset: z
                      .enum([
                        'UNRECOGNISED',
                        'PHYSICAL',
                        'DIGITAL',
                        'GIFT_CARD',
                        'SERVICE',
                      ])
                      .describe('Preset item type.'),
                  }),
                  z.object({
                    preset: z.never().optional(),
                    custom: z
                      .string()
                      .describe(
                        'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                      ),
                  }),
                ])
              )
              .describe('Item type. Either a preset type or custom.'),
            fullPrice: z
              .string()
              .describe(
                'Item price **before** catalog-defined discount. Defaults to `price` when not provided.'
              )
              .optional()
              .nullable(),
            quantityAvailable: z
              .number()
              .int()
              .describe(
                'Item quantity available for purchase. Only return this if inventory is managed.\nNot returning this field means that the buyer can "infinitely" tick up the number of items in the cart.'
              )
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            physicalProperties: z
              .object({
                weight: z
                  .number()
                  .describe(
                    'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe(
                    'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                  )
                  .max(40)
                  .optional()
                  .nullable(),
                shippable: z
                  .boolean()
                  .describe('Whether this line item is shippable.')
                  .optional(),
              })
              .describe('Physical properties of the item.')
              .optional(),
            paymentOption: z
              .enum([
                'FULL_PAYMENT_ONLINE',
                'FULL_PAYMENT_OFFLINE',
                'MEMBERSHIP',
                'DEPOSIT_ONLINE',
                'MEMBERSHIP_OFFLINE',
                'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
              ])
              .optional(),
            serviceProperties: z
              .object({
                scheduledDate: z
                  .date()
                  .describe(
                    'Date and time the service is to be provided, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nFor example, the start time of a class.'
                  )
                  .optional()
                  .nullable(),
                numberOfParticipants: z
                  .number()
                  .int()
                  .describe(
                    'The number of people participating in the service. For example, the number of people attending a class or the number of people per hotel room.'
                  )
                  .min(1)
                  .max(10000)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Service properties. When relevant, this contains information such as date and number of participants.\nUsed, among other things, when checking for valid memberships.'
              )
              .optional(),
            rootCatalogItemId: z
              .string()
              .describe(
                "In cases where `catalogReference.catalogItemId` is NOT the actual catalog item ID, this field will return the true item's ID.\n\n+ For example, for Wix Bookings, `catalogReference.catalogItemId` is the booking ID. Therefore this value is set to the service ID.\n+ In most cases, this field is the same as `catalogReference.catalogItemId`.\n+ Used in membership validation."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            depositAmount: z
              .string()
              .describe(
                "Partial payment for the given item to be paid upfront during the checkout.\n\nEligible for catalog items with type `DEPOSIT_ONLINE`.\nWhen omitted, the item's price will not be split and is expected to be paid in a single installment."
              )
              .optional()
              .nullable(),
            deliveryProfileId: z
              .string()
              .describe('Delivery profile ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            catalogReference: z
              .object({
                catalogItemId: z
                  .string()
                  .describe('ID of the item within the catalog it belongs to.')
                  .min(1)
                  .max(36),
                appId: z
                  .string()
                  .describe(
                    'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                  )
                  .min(1),
                options: z
                  .record(z.string(), z.any())
                  .describe(
                    'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Catalog and item reference. Includes IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
              )
              .optional(),
            priceUndetermined: z
              .boolean()
              .describe(
                'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
              )
              .optional(),
            fixedQuantity: z
              .boolean()
              .describe(
                'Whether the line item quantity is fixed and cannot be changed.\n\nDefault: `false`'
              )
              .optional(),
            consentRequiredPaymentPolicy: z
              .string()
              .describe(
                'Item payment policy that requires customer consent to complete purchase. The payment policy will be displayed on the checkout page.'
              )
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
            savePaymentMethod: z
              .boolean()
              .describe(
                'Whether to save the payment method on the order.\n\nDefault: `false`'
              )
              .optional(),
            policies: z
              .array(
                z.object({
                  title: z
                    .string()
                    .describe('Policy title - should be translated')
                    .min(1)
                    .max(29)
                    .optional()
                    .nullable(),
                  content: z
                    .string()
                    .describe('Policy content - should be translated')
                    .min(1)
                    .max(3000)
                    .optional(),
                })
              )
              .max(5)
              .optional(),
            membersOnly: z
              .boolean()
              .describe(
                'Whether the item can only be purchased by site members.\n\nDefault: `false`'
              )
              .optional(),
            taxableAddress: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ addressType: z.never().optional() }),
                  z.object({
                    addressType: z
                      .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                      .describe(
                        'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                      ),
                  }),
                ])
              )
              .describe('Address to use for tax calculation purposes.')
              .optional(),
            modifierGroups: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Modifier group ID.')
                    .min(1)
                    .max(36)
                    .optional(),
                  name: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe('Modifier group name.')
                    .optional(),
                  modifiers: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Modifier ID.')
                          .min(1)
                          .max(36)
                          .optional(),
                        quantity: z
                          .number()
                          .int()
                          .describe('The quantity of this modifier.')
                          .min(1)
                          .max(100000)
                          .optional()
                          .nullable(),
                        label: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Primary display label for the modifier.')
                          .optional(),
                        details: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Additional details.')
                          .optional(),
                        price: z
                          .string()
                          .describe('The price of the modifier.')
                          .optional()
                          .nullable(),
                      })
                    )
                    .min(1)
                    .max(25)
                    .optional(),
                })
              )
              .max(25)
              .optional(),
          })
        )
        .max(300)
        .optional(),
      couponCode: z
        .string()
        .describe(
          'Coupon code.\n\nNote that a checkout can only hold one `couponCode` at a time. If an additional `couponCode` is added, it will override the existing `couponCode`.\nFor additional information, see the Coupons API.'
        )
        .optional()
        .nullable(),
      customContentReference: z
        .object({
          appId: z
            .string()
            .describe(
              "ID of the app providing the content.\n\nYou can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          componentId: z
            .string()
            .describe(
              "ID of the component within the app it belongs to.\n\nYou can get your component's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          'Reference IDs for the app and component providing custom checkout page content.\n\nTo access and manage custom checkout page content, your app must have the permission scope named "Manage eCommerce - Admin Permissions".\nLearn more about [permission scopes](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/about-permissions).'
        )
        .optional(),
      externalReference: z
        .object({
          appId: z
            .string()
            .describe(
              'ID of the app associated with the purchase flow.\nFor example, the Wix Pay Links app ID.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          resourceId: z
            .string()
            .describe(
              'Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.\nFor example, a Wix Pay Link ID.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
        })
        .describe(
          'References to an external app and resource associated with the checkout.\nUsed for integration and tracking across different platforms.'
        )
        .optional(),
    })
    .describe('Checkout template to create.'),
});
export const CreateCheckoutTemplateResponse = z.object({
  _id: z
    .string()
    .describe('Checkout template ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  status: z
    .enum(['ACTIVE', 'INACTIVE'])
    .describe(
      'Status of the checkout template.\n\nWhen `status` is `INACTIVE` checkouts will not be created with this template `id`. Instead, the endpoint will redirect to the domain site.\n\nDefault: `ACTIVE`'
    )
    .optional(),
  customization: z
    .object({
      webClient: z
        .object({
          disabledContinueShoppingButton: z
            .boolean()
            .describe(
              'Whether to disable the "Continue shopping" button.\n\nDefault: `false`'
            )
            .optional(),
          disabledPolicyAgreementCheckbox: z
            .boolean()
            .describe(
              'Whether to disable policy agreement checkout in the checkout page\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe(
          'Web client customizations. These customizations only apply to the standard Wix checkout page.'
        )
        .optional(),
      lockedGiftCard: z
        .boolean()
        .describe(
          'Whether to lock the ability to add or remove a gift card.\n\nDefault: `false`'
        )
        .optional(),
      lockedCoupon: z
        .boolean()
        .describe(
          'Whether to lock the ability to apply or remove a coupon code.\n\nDefault: `false`'
        )
        .optional(),
      disabledManualPayment: z
        .boolean()
        .describe(
          'Whether to disable manual payment option for this checkout.\n\nDefault: `false`'
        )
        .optional(),
    })
    .describe(
      'Custom settings to apply to the checkout page created from this template.'
    )
    .optional(),
  lineItems: z
    .array(
      z.object({
        quantity: z
          .number()
          .int()
          .describe('Item quantity.\n\nMin: `1` <br />\nMax: `100000`')
          .min(1)
          .max(100000)
          .optional(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Catalog and item reference. Holds IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
          )
          .optional(),
        catalogOverrideFields: z
          .object({
            productName: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe('Item name.')
              .optional(),
            price: z
              .string()
              .describe('Item price **after** discounts.')
              .optional()
              .nullable(),
            fullPrice: z
              .string()
              .describe('Item price **before** discounts.')
              .optional()
              .nullable(),
            descriptionLines: z
              .array(
                z.intersection(
                  z.object({
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                          )
                          .max(100)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                      .describe('Description line name.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z.never().optional(),
                    }),
                    z.object({
                      colorInfo: z.never().optional(),
                      plainText: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(600)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(600)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line plain text value.'),
                    }),
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(500)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          code: z
                            .string()
                            .describe('HEX or RGB color code for display.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line color value.'),
                    }),
                  ])
                )
              )
              .min(0)
              .max(20)
              .optional(),
            physicalProperties: z
              .object({
                weight: z
                  .number()
                  .describe(
                    'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe(
                    'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                  )
                  .max(40)
                  .optional()
                  .nullable(),
                shippable: z
                  .boolean()
                  .describe('Whether this line item is shippable.')
                  .optional(),
              })
              .describe('Physical properties of the item.')
              .optional(),
            image: z.string().describe('Item image.').optional(),
            paymentOption: z
              .object({
                value: z
                  .enum([
                    'FULL_PAYMENT_ONLINE',
                    'FULL_PAYMENT_OFFLINE',
                    'MEMBERSHIP',
                    'DEPOSIT_ONLINE',
                    'MEMBERSHIP_OFFLINE',
                    'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                  ])
                  .describe(
                    'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
                  )
                  .optional(),
              })
              .describe('Payment method selected for the item.')
              .optional(),
            depositAmount: z
              .string()
              .describe(
                'Only eligible for catalog items with `lineItem.paymentOption.value` type of `DEPOSIT_ONLINE`.'
              )
              .optional()
              .nullable(),
            savePaymentMethod: z
              .boolean()
              .describe(
                'Whether to save the payment method on the order.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
            inventoryAppId: z
              .string()
              .describe('ID of the app managing the inventory.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe('Overriding values for catalog item properties.')
          .optional(),
      })
    )
    .max(300)
    .optional(),
  customLineItems: z
    .array(
      z.object({
        quantity: z
          .number()
          .int()
          .describe('Custom line item quantity.\n\nMin: `1`\nMax: `100000`')
          .min(1)
          .max(100000)
          .optional(),
        price: z
          .string()
          .describe(
            'Custom line item price.\n\nMust be a number or a decimal without symbols.'
          )
          .optional(),
        priceDescription: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
              )
              .min(1)
              .max(100)
              .optional(),
            translated: z
              .string()
              .describe(
                "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
          })
          .describe(
            'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
          )
          .optional(),
        descriptionLines: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                      )
                      .max(100)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe('Description line name.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z.never().optional(),
                }),
                z.object({
                  colorInfo: z.never().optional(),
                  plainText: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(600)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(600)
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line plain text value.'),
                }),
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(500)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      code: z
                        .string()
                        .describe('HEX or RGB color code for display.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line color value.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        media: z.string().describe("Custom line item's media.").optional(),
        _id: z
          .string()
          .describe(
            'Deprecated. Custom line item ID.\n\nTo maintain a unique value for a custom line item across multiple carts and orders, pass `catalogReference` instead.\nLearn more about [custom items with catalog reference](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/catalogs/catalog-and-custom-items#custom-item-with-catalog-reference).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        taxGroupId: z
          .string()
          .describe('Tax group ID for this custom line item.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        productName: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
              )
              .min(1)
              .max(200)
              .optional(),
            translated: z
              .string()
              .describe(
                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
              )
              .min(1)
              .max(400)
              .optional()
              .nullable(),
          })
          .describe('Name of the item or product.')
          .optional(),
        url: z
          .string()
          .describe(
            "URL to the item's page on the site. When not provided, the link back from the cart page to the relevant product page will not work."
          )
          .optional(),
        itemType: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                preset: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                preset: z
                  .enum([
                    'UNRECOGNISED',
                    'PHYSICAL',
                    'DIGITAL',
                    'GIFT_CARD',
                    'SERVICE',
                  ])
                  .describe('Preset item type.'),
              }),
              z.object({
                preset: z.never().optional(),
                custom: z
                  .string()
                  .describe(
                    'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                  ),
              }),
            ])
          )
          .describe('Item type. Either a preset type or custom.')
          .optional(),
        fullPrice: z
          .string()
          .describe(
            'Item price **before** catalog-defined discount. Defaults to `price` when not provided.'
          )
          .optional()
          .nullable(),
        quantityAvailable: z
          .number()
          .int()
          .describe(
            'Item quantity available for purchase. Only return this if inventory is managed.\nNot returning this field means that the buyer can "infinitely" tick up the number of items in the cart.'
          )
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        physicalProperties: z
          .object({
            weight: z
              .number()
              .describe(
                'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
              )
              .optional()
              .nullable(),
            sku: z
              .string()
              .describe(
                'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
              )
              .max(40)
              .optional()
              .nullable(),
            shippable: z
              .boolean()
              .describe('Whether this line item is shippable.')
              .optional(),
          })
          .describe('Physical properties of the item.')
          .optional(),
        paymentOption: z
          .enum([
            'FULL_PAYMENT_ONLINE',
            'FULL_PAYMENT_OFFLINE',
            'MEMBERSHIP',
            'DEPOSIT_ONLINE',
            'MEMBERSHIP_OFFLINE',
            'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
          ])
          .describe(
            'Type of selected payment option for current item. Defaults to `FULL_PAYMENT_ONLINE`.\n\n+ `FULL_PAYMENT_ONLINE` - Entire payment for this item happens as part of the checkout.\n+ `FULL_PAYMENT_OFFLINE` - Entire payment for this item happens after the checkout. For example, when using cash, check, or other offline payment methods.\n+ `MEMBERSHIP` - Payment for this item is done by charging a membership. When this option is used, `lineItem.price.amount` is 0.\n+ `DEPOSIT_ONLINE` -  Partial payment for the given item to be paid upfront during the checkout. Amount to be paid is defined by deposit_amount field.'
          )
          .optional(),
        serviceProperties: z
          .object({
            scheduledDate: z
              .date()
              .describe(
                'Date and time the service is to be provided, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nFor example, the start time of a class.'
              )
              .optional()
              .nullable(),
            numberOfParticipants: z
              .number()
              .int()
              .describe(
                'The number of people participating in the service. For example, the number of people attending a class or the number of people per hotel room.'
              )
              .min(1)
              .max(10000)
              .optional()
              .nullable(),
          })
          .describe(
            'Service properties. When relevant, this contains information such as date and number of participants.\nUsed, among other things, when checking for valid memberships.'
          )
          .optional(),
        rootCatalogItemId: z
          .string()
          .describe(
            "In cases where `catalogReference.catalogItemId` is NOT the actual catalog item ID, this field will return the true item's ID.\n\n+ For example, for Wix Bookings, `catalogReference.catalogItemId` is the booking ID. Therefore this value is set to the service ID.\n+ In most cases, this field is the same as `catalogReference.catalogItemId`.\n+ Used in membership validation."
          )
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        depositAmount: z
          .string()
          .describe(
            "Partial payment for the given item to be paid upfront during the checkout.\n\nEligible for catalog items with type `DEPOSIT_ONLINE`.\nWhen omitted, the item's price will not be split and is expected to be paid in a single installment."
          )
          .optional()
          .nullable(),
        deliveryProfileId: z
          .string()
          .describe('Delivery profile ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Catalog and item reference. Includes IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
          )
          .optional(),
        priceUndetermined: z
          .boolean()
          .describe(
            'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
          )
          .optional(),
        fixedQuantity: z
          .boolean()
          .describe(
            'Whether the line item quantity is fixed and cannot be changed.\n\nDefault: `false`'
          )
          .optional(),
        consentRequiredPaymentPolicy: z
          .string()
          .describe(
            'Item payment policy that requires customer consent to complete purchase. The payment policy will be displayed on the checkout page.'
          )
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
        savePaymentMethod: z
          .boolean()
          .describe(
            'Whether to save the payment method on the order.\n\nDefault: `false`'
          )
          .optional(),
        policies: z
          .array(
            z.object({
              title: z
                .string()
                .describe('Policy title - should be translated')
                .min(1)
                .max(29)
                .optional()
                .nullable(),
              content: z
                .string()
                .describe('Policy content - should be translated')
                .min(1)
                .max(3000)
                .optional(),
            })
          )
          .max(5)
          .optional(),
        membersOnly: z
          .boolean()
          .describe(
            'Whether the item can only be purchased by site members.\n\nDefault: `false`'
          )
          .optional(),
        taxableAddress: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ addressType: z.never().optional() }),
              z.object({
                addressType: z
                  .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                  .describe(
                    'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                  ),
              }),
            ])
          )
          .describe('Address to use for tax calculation purposes.')
          .optional(),
        modifierGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Modifier group ID.')
                .min(1)
                .max(36)
                .optional(),
              name: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe('Modifier group name.')
                .optional(),
              modifiers: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe('The quantity of this modifier.')
                      .min(1)
                      .max(100000)
                      .optional()
                      .nullable(),
                    label: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Primary display label for the modifier.')
                      .optional(),
                    details: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Additional details.')
                      .optional(),
                    price: z
                      .string()
                      .describe('The price of the modifier.')
                      .optional()
                      .nullable(),
                  })
                )
                .min(1)
                .max(25)
                .optional(),
            })
          )
          .max(25)
          .optional(),
      })
    )
    .max(300)
    .optional(),
  couponCode: z
    .string()
    .describe(
      'Coupon code.\n\nNote that a checkout can only hold one `couponCode` at a time. If an additional `couponCode` is added, it will override the existing `couponCode`.\nFor additional information, see the Coupons API.'
    )
    .optional()
    .nullable(),
  customContentReference: z
    .object({
      appId: z
        .string()
        .describe(
          "ID of the app providing the content.\n\nYou can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      componentId: z
        .string()
        .describe(
          "ID of the component within the app it belongs to.\n\nYou can get your component's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
    })
    .describe(
      'Reference IDs for the app and component providing custom checkout page content.\n\nTo access and manage custom checkout page content, your app must have the permission scope named "Manage eCommerce - Admin Permissions".\nLearn more about [permission scopes](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/about-permissions).'
    )
    .optional(),
  externalReference: z
    .object({
      appId: z
        .string()
        .describe(
          'ID of the app associated with the purchase flow.\nFor example, the Wix Pay Links app ID.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      resourceId: z
        .string()
        .describe(
          'Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.\nFor example, a Wix Pay Link ID.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
    })
    .describe(
      'References to an external app and resource associated with the checkout.\nUsed for integration and tracking across different platforms.'
    )
    .optional(),
});
export const GetCheckoutTemplateRequest = z.object({
  checkoutTemplateId: z
    .string()
    .describe('ID of the checkout template to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetCheckoutTemplateResponse = z.object({
  _id: z
    .string()
    .describe('Checkout template ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  status: z
    .enum(['ACTIVE', 'INACTIVE'])
    .describe(
      'Status of the checkout template.\n\nWhen `status` is `INACTIVE` checkouts will not be created with this template `id`. Instead, the endpoint will redirect to the domain site.\n\nDefault: `ACTIVE`'
    )
    .optional(),
  customization: z
    .object({
      webClient: z
        .object({
          disabledContinueShoppingButton: z
            .boolean()
            .describe(
              'Whether to disable the "Continue shopping" button.\n\nDefault: `false`'
            )
            .optional(),
          disabledPolicyAgreementCheckbox: z
            .boolean()
            .describe(
              'Whether to disable policy agreement checkout in the checkout page\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe(
          'Web client customizations. These customizations only apply to the standard Wix checkout page.'
        )
        .optional(),
      lockedGiftCard: z
        .boolean()
        .describe(
          'Whether to lock the ability to add or remove a gift card.\n\nDefault: `false`'
        )
        .optional(),
      lockedCoupon: z
        .boolean()
        .describe(
          'Whether to lock the ability to apply or remove a coupon code.\n\nDefault: `false`'
        )
        .optional(),
      disabledManualPayment: z
        .boolean()
        .describe(
          'Whether to disable manual payment option for this checkout.\n\nDefault: `false`'
        )
        .optional(),
    })
    .describe(
      'Custom settings to apply to the checkout page created from this template.'
    )
    .optional(),
  lineItems: z
    .array(
      z.object({
        quantity: z
          .number()
          .int()
          .describe('Item quantity.\n\nMin: `1` <br />\nMax: `100000`')
          .min(1)
          .max(100000)
          .optional(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Catalog and item reference. Holds IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
          )
          .optional(),
        catalogOverrideFields: z
          .object({
            productName: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe('Item name.')
              .optional(),
            price: z
              .string()
              .describe('Item price **after** discounts.')
              .optional()
              .nullable(),
            fullPrice: z
              .string()
              .describe('Item price **before** discounts.')
              .optional()
              .nullable(),
            descriptionLines: z
              .array(
                z.intersection(
                  z.object({
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                          )
                          .max(100)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                      .describe('Description line name.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z.never().optional(),
                    }),
                    z.object({
                      colorInfo: z.never().optional(),
                      plainText: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(600)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(600)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line plain text value.'),
                    }),
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(500)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          code: z
                            .string()
                            .describe('HEX or RGB color code for display.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line color value.'),
                    }),
                  ])
                )
              )
              .min(0)
              .max(20)
              .optional(),
            physicalProperties: z
              .object({
                weight: z
                  .number()
                  .describe(
                    'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe(
                    'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                  )
                  .max(40)
                  .optional()
                  .nullable(),
                shippable: z
                  .boolean()
                  .describe('Whether this line item is shippable.')
                  .optional(),
              })
              .describe('Physical properties of the item.')
              .optional(),
            image: z.string().describe('Item image.').optional(),
            paymentOption: z
              .object({
                value: z
                  .enum([
                    'FULL_PAYMENT_ONLINE',
                    'FULL_PAYMENT_OFFLINE',
                    'MEMBERSHIP',
                    'DEPOSIT_ONLINE',
                    'MEMBERSHIP_OFFLINE',
                    'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                  ])
                  .describe(
                    'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
                  )
                  .optional(),
              })
              .describe('Payment method selected for the item.')
              .optional(),
            depositAmount: z
              .string()
              .describe(
                'Only eligible for catalog items with `lineItem.paymentOption.value` type of `DEPOSIT_ONLINE`.'
              )
              .optional()
              .nullable(),
            savePaymentMethod: z
              .boolean()
              .describe(
                'Whether to save the payment method on the order.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
            inventoryAppId: z
              .string()
              .describe('ID of the app managing the inventory.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe('Overriding values for catalog item properties.')
          .optional(),
      })
    )
    .max(300)
    .optional(),
  customLineItems: z
    .array(
      z.object({
        quantity: z
          .number()
          .int()
          .describe('Custom line item quantity.\n\nMin: `1`\nMax: `100000`')
          .min(1)
          .max(100000)
          .optional(),
        price: z
          .string()
          .describe(
            'Custom line item price.\n\nMust be a number or a decimal without symbols.'
          )
          .optional(),
        priceDescription: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
              )
              .min(1)
              .max(100)
              .optional(),
            translated: z
              .string()
              .describe(
                "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
          })
          .describe(
            'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
          )
          .optional(),
        descriptionLines: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                      )
                      .max(100)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe('Description line name.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z.never().optional(),
                }),
                z.object({
                  colorInfo: z.never().optional(),
                  plainText: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(600)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(600)
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line plain text value.'),
                }),
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(500)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      code: z
                        .string()
                        .describe('HEX or RGB color code for display.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line color value.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        media: z.string().describe("Custom line item's media.").optional(),
        _id: z
          .string()
          .describe(
            'Deprecated. Custom line item ID.\n\nTo maintain a unique value for a custom line item across multiple carts and orders, pass `catalogReference` instead.\nLearn more about [custom items with catalog reference](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/catalogs/catalog-and-custom-items#custom-item-with-catalog-reference).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        taxGroupId: z
          .string()
          .describe('Tax group ID for this custom line item.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        productName: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
              )
              .min(1)
              .max(200)
              .optional(),
            translated: z
              .string()
              .describe(
                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
              )
              .min(1)
              .max(400)
              .optional()
              .nullable(),
          })
          .describe('Name of the item or product.')
          .optional(),
        url: z
          .string()
          .describe(
            "URL to the item's page on the site. When not provided, the link back from the cart page to the relevant product page will not work."
          )
          .optional(),
        itemType: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                preset: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                preset: z
                  .enum([
                    'UNRECOGNISED',
                    'PHYSICAL',
                    'DIGITAL',
                    'GIFT_CARD',
                    'SERVICE',
                  ])
                  .describe('Preset item type.'),
              }),
              z.object({
                preset: z.never().optional(),
                custom: z
                  .string()
                  .describe(
                    'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                  ),
              }),
            ])
          )
          .describe('Item type. Either a preset type or custom.')
          .optional(),
        fullPrice: z
          .string()
          .describe(
            'Item price **before** catalog-defined discount. Defaults to `price` when not provided.'
          )
          .optional()
          .nullable(),
        quantityAvailable: z
          .number()
          .int()
          .describe(
            'Item quantity available for purchase. Only return this if inventory is managed.\nNot returning this field means that the buyer can "infinitely" tick up the number of items in the cart.'
          )
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        physicalProperties: z
          .object({
            weight: z
              .number()
              .describe(
                'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
              )
              .optional()
              .nullable(),
            sku: z
              .string()
              .describe(
                'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
              )
              .max(40)
              .optional()
              .nullable(),
            shippable: z
              .boolean()
              .describe('Whether this line item is shippable.')
              .optional(),
          })
          .describe('Physical properties of the item.')
          .optional(),
        paymentOption: z
          .enum([
            'FULL_PAYMENT_ONLINE',
            'FULL_PAYMENT_OFFLINE',
            'MEMBERSHIP',
            'DEPOSIT_ONLINE',
            'MEMBERSHIP_OFFLINE',
            'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
          ])
          .describe(
            'Type of selected payment option for current item. Defaults to `FULL_PAYMENT_ONLINE`.\n\n+ `FULL_PAYMENT_ONLINE` - Entire payment for this item happens as part of the checkout.\n+ `FULL_PAYMENT_OFFLINE` - Entire payment for this item happens after the checkout. For example, when using cash, check, or other offline payment methods.\n+ `MEMBERSHIP` - Payment for this item is done by charging a membership. When this option is used, `lineItem.price.amount` is 0.\n+ `DEPOSIT_ONLINE` -  Partial payment for the given item to be paid upfront during the checkout. Amount to be paid is defined by deposit_amount field.'
          )
          .optional(),
        serviceProperties: z
          .object({
            scheduledDate: z
              .date()
              .describe(
                'Date and time the service is to be provided, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nFor example, the start time of a class.'
              )
              .optional()
              .nullable(),
            numberOfParticipants: z
              .number()
              .int()
              .describe(
                'The number of people participating in the service. For example, the number of people attending a class or the number of people per hotel room.'
              )
              .min(1)
              .max(10000)
              .optional()
              .nullable(),
          })
          .describe(
            'Service properties. When relevant, this contains information such as date and number of participants.\nUsed, among other things, when checking for valid memberships.'
          )
          .optional(),
        rootCatalogItemId: z
          .string()
          .describe(
            "In cases where `catalogReference.catalogItemId` is NOT the actual catalog item ID, this field will return the true item's ID.\n\n+ For example, for Wix Bookings, `catalogReference.catalogItemId` is the booking ID. Therefore this value is set to the service ID.\n+ In most cases, this field is the same as `catalogReference.catalogItemId`.\n+ Used in membership validation."
          )
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        depositAmount: z
          .string()
          .describe(
            "Partial payment for the given item to be paid upfront during the checkout.\n\nEligible for catalog items with type `DEPOSIT_ONLINE`.\nWhen omitted, the item's price will not be split and is expected to be paid in a single installment."
          )
          .optional()
          .nullable(),
        deliveryProfileId: z
          .string()
          .describe('Delivery profile ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Catalog and item reference. Includes IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
          )
          .optional(),
        priceUndetermined: z
          .boolean()
          .describe(
            'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
          )
          .optional(),
        fixedQuantity: z
          .boolean()
          .describe(
            'Whether the line item quantity is fixed and cannot be changed.\n\nDefault: `false`'
          )
          .optional(),
        consentRequiredPaymentPolicy: z
          .string()
          .describe(
            'Item payment policy that requires customer consent to complete purchase. The payment policy will be displayed on the checkout page.'
          )
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
        savePaymentMethod: z
          .boolean()
          .describe(
            'Whether to save the payment method on the order.\n\nDefault: `false`'
          )
          .optional(),
        policies: z
          .array(
            z.object({
              title: z
                .string()
                .describe('Policy title - should be translated')
                .min(1)
                .max(29)
                .optional()
                .nullable(),
              content: z
                .string()
                .describe('Policy content - should be translated')
                .min(1)
                .max(3000)
                .optional(),
            })
          )
          .max(5)
          .optional(),
        membersOnly: z
          .boolean()
          .describe(
            'Whether the item can only be purchased by site members.\n\nDefault: `false`'
          )
          .optional(),
        taxableAddress: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ addressType: z.never().optional() }),
              z.object({
                addressType: z
                  .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                  .describe(
                    'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                  ),
              }),
            ])
          )
          .describe('Address to use for tax calculation purposes.')
          .optional(),
        modifierGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Modifier group ID.')
                .min(1)
                .max(36)
                .optional(),
              name: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe('Modifier group name.')
                .optional(),
              modifiers: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe('The quantity of this modifier.')
                      .min(1)
                      .max(100000)
                      .optional()
                      .nullable(),
                    label: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Primary display label for the modifier.')
                      .optional(),
                    details: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Additional details.')
                      .optional(),
                    price: z
                      .string()
                      .describe('The price of the modifier.')
                      .optional()
                      .nullable(),
                  })
                )
                .min(1)
                .max(25)
                .optional(),
            })
          )
          .max(25)
          .optional(),
      })
    )
    .max(300)
    .optional(),
  couponCode: z
    .string()
    .describe(
      'Coupon code.\n\nNote that a checkout can only hold one `couponCode` at a time. If an additional `couponCode` is added, it will override the existing `couponCode`.\nFor additional information, see the Coupons API.'
    )
    .optional()
    .nullable(),
  customContentReference: z
    .object({
      appId: z
        .string()
        .describe(
          "ID of the app providing the content.\n\nYou can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      componentId: z
        .string()
        .describe(
          "ID of the component within the app it belongs to.\n\nYou can get your component's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
    })
    .describe(
      'Reference IDs for the app and component providing custom checkout page content.\n\nTo access and manage custom checkout page content, your app must have the permission scope named "Manage eCommerce - Admin Permissions".\nLearn more about [permission scopes](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/about-permissions).'
    )
    .optional(),
  externalReference: z
    .object({
      appId: z
        .string()
        .describe(
          'ID of the app associated with the purchase flow.\nFor example, the Wix Pay Links app ID.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      resourceId: z
        .string()
        .describe(
          'Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.\nFor example, a Wix Pay Link ID.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
    })
    .describe(
      'References to an external app and resource associated with the checkout.\nUsed for integration and tracking across different platforms.'
    )
    .optional(),
});
export const UpdateCheckoutTemplateRequest = z.object({
  _id: z
    .string()
    .describe('Checkout template ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  checkoutTemplate: z
    .object({
      _id: z
        .string()
        .describe('Checkout template ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
      customization: z
        .object({
          webClient: z
            .object({
              disabledContinueShoppingButton: z
                .boolean()
                .describe(
                  'Whether to disable the "Continue shopping" button.\n\nDefault: `false`'
                )
                .optional(),
              disabledPolicyAgreementCheckbox: z
                .boolean()
                .describe(
                  'Whether to disable policy agreement checkout in the checkout page\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              'Web client customizations. These customizations only apply to the standard Wix checkout page.'
            )
            .optional(),
          lockedGiftCard: z
            .boolean()
            .describe(
              'Whether to lock the ability to add or remove a gift card.\n\nDefault: `false`'
            )
            .optional(),
          lockedCoupon: z
            .boolean()
            .describe(
              'Whether to lock the ability to apply or remove a coupon code.\n\nDefault: `false`'
            )
            .optional(),
          disabledManualPayment: z
            .boolean()
            .describe(
              'Whether to disable manual payment option for this checkout.\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe(
          'Custom settings to apply to the checkout page created from this template.'
        )
        .optional(),
      lineItems: z
        .array(
          z.object({
            quantity: z
              .number()
              .int()
              .describe('Item quantity.\n\nMin: `1` <br />\nMax: `100000`')
              .min(1)
              .max(100000)
              .optional(),
            catalogReference: z
              .object({
                catalogItemId: z
                  .string()
                  .describe('ID of the item within the catalog it belongs to.')
                  .min(1)
                  .max(36)
                  .optional(),
                appId: z
                  .string()
                  .describe(
                    'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                  )
                  .min(1)
                  .optional(),
                options: z
                  .record(z.string(), z.any())
                  .describe(
                    'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Catalog and item reference. Holds IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
              )
              .optional(),
            catalogOverrideFields: z
              .object({
                productName: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                      )
                      .min(1)
                      .max(200)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                      )
                      .min(1)
                      .max(400)
                      .optional()
                      .nullable(),
                  })
                  .describe('Item name.')
                  .optional(),
                price: z
                  .string()
                  .describe('Item price **after** discounts.')
                  .optional()
                  .nullable(),
                fullPrice: z
                  .string()
                  .describe('Item price **before** discounts.')
                  .optional()
                  .nullable(),
                descriptionLines: z
                  .array(
                    z.intersection(
                      z.object({
                        name: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                              )
                              .max(100)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                          })
                          .describe('Description line name.')
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          plainText: z.never().optional(),
                          colorInfo: z.never().optional(),
                        }),
                        z.object({
                          colorInfo: z.never().optional(),
                          plainText: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                )
                                .max(600)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                                )
                                .max(600)
                                .optional()
                                .nullable(),
                            })
                            .describe('Description line plain text value.'),
                        }),
                        z.object({
                          plainText: z.never().optional(),
                          colorInfo: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                )
                                .max(500)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                                )
                                .max(500)
                                .optional()
                                .nullable(),
                              code: z
                                .string()
                                .describe('HEX or RGB color code for display.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Description line color value.'),
                        }),
                      ])
                    )
                  )
                  .min(0)
                  .max(20)
                  .optional(),
                physicalProperties: z
                  .object({
                    weight: z
                      .number()
                      .describe(
                        'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                      )
                      .optional()
                      .nullable(),
                    sku: z
                      .string()
                      .describe(
                        'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                      )
                      .max(40)
                      .optional()
                      .nullable(),
                    shippable: z
                      .boolean()
                      .describe('Whether this line item is shippable.')
                      .optional(),
                  })
                  .describe('Physical properties of the item.')
                  .optional(),
                image: z.string().describe('Item image.').optional(),
                paymentOption: z
                  .object({
                    value: z
                      .enum([
                        'FULL_PAYMENT_ONLINE',
                        'FULL_PAYMENT_OFFLINE',
                        'MEMBERSHIP',
                        'DEPOSIT_ONLINE',
                        'MEMBERSHIP_OFFLINE',
                        'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                      ])
                      .optional(),
                  })
                  .describe('Payment method selected for the item.')
                  .optional(),
                depositAmount: z
                  .string()
                  .describe(
                    'Only eligible for catalog items with `lineItem.paymentOption.value` type of `DEPOSIT_ONLINE`.'
                  )
                  .optional()
                  .nullable(),
                savePaymentMethod: z
                  .boolean()
                  .describe(
                    'Whether to save the payment method on the order.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                inventoryAppId: z
                  .string()
                  .describe('ID of the app managing the inventory.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Overriding values for catalog item properties.')
              .optional(),
          })
        )
        .max(300)
        .optional(),
      customLineItems: z
        .array(
          z.object({
            quantity: z
              .number()
              .int()
              .describe('Custom line item quantity.\n\nMin: `1`\nMax: `100000`')
              .min(1)
              .max(100000)
              .optional(),
            price: z
              .string()
              .describe(
                'Custom line item price.\n\nMust be a number or a decimal without symbols.'
              )
              .optional(),
            priceDescription: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                  )
                  .min(1)
                  .max(100)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
              )
              .optional(),
            descriptionLines: z
              .array(
                z.intersection(
                  z.object({
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                          )
                          .max(100)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                      .describe('Description line name.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z.never().optional(),
                    }),
                    z.object({
                      colorInfo: z.never().optional(),
                      plainText: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(600)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(600)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line plain text value.'),
                    }),
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(500)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          code: z
                            .string()
                            .describe('HEX or RGB color code for display.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line color value.'),
                    }),
                  ])
                )
              )
              .max(10)
              .optional(),
            media: z.string().describe("Custom line item's media.").optional(),
            _id: z
              .string()
              .describe(
                'Deprecated. Custom line item ID.\n\nTo maintain a unique value for a custom line item across multiple carts and orders, pass `catalogReference` instead.\nLearn more about [custom items with catalog reference](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/catalogs/catalog-and-custom-items#custom-item-with-catalog-reference).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe('Tax group ID for this custom line item.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            productName: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe('Name of the item or product.')
              .optional(),
            url: z
              .string()
              .describe(
                "URL to the item's page on the site. When not provided, the link back from the cart page to the relevant product page will not work."
              )
              .optional(),
            itemType: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    preset: z.never().optional(),
                    custom: z.never().optional(),
                  }),
                  z.object({
                    custom: z.never().optional(),
                    preset: z
                      .enum([
                        'UNRECOGNISED',
                        'PHYSICAL',
                        'DIGITAL',
                        'GIFT_CARD',
                        'SERVICE',
                      ])
                      .describe('Preset item type.'),
                  }),
                  z.object({
                    preset: z.never().optional(),
                    custom: z
                      .string()
                      .describe(
                        'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                      ),
                  }),
                ])
              )
              .describe('Item type. Either a preset type or custom.')
              .optional(),
            fullPrice: z
              .string()
              .describe(
                'Item price **before** catalog-defined discount. Defaults to `price` when not provided.'
              )
              .optional()
              .nullable(),
            quantityAvailable: z
              .number()
              .int()
              .describe(
                'Item quantity available for purchase. Only return this if inventory is managed.\nNot returning this field means that the buyer can "infinitely" tick up the number of items in the cart.'
              )
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            physicalProperties: z
              .object({
                weight: z
                  .number()
                  .describe(
                    'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe(
                    'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                  )
                  .max(40)
                  .optional()
                  .nullable(),
                shippable: z
                  .boolean()
                  .describe('Whether this line item is shippable.')
                  .optional(),
              })
              .describe('Physical properties of the item.')
              .optional(),
            paymentOption: z
              .enum([
                'FULL_PAYMENT_ONLINE',
                'FULL_PAYMENT_OFFLINE',
                'MEMBERSHIP',
                'DEPOSIT_ONLINE',
                'MEMBERSHIP_OFFLINE',
                'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
              ])
              .optional(),
            serviceProperties: z
              .object({
                scheduledDate: z
                  .date()
                  .describe(
                    'Date and time the service is to be provided, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nFor example, the start time of a class.'
                  )
                  .optional()
                  .nullable(),
                numberOfParticipants: z
                  .number()
                  .int()
                  .describe(
                    'The number of people participating in the service. For example, the number of people attending a class or the number of people per hotel room.'
                  )
                  .min(1)
                  .max(10000)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Service properties. When relevant, this contains information such as date and number of participants.\nUsed, among other things, when checking for valid memberships.'
              )
              .optional(),
            rootCatalogItemId: z
              .string()
              .describe(
                "In cases where `catalogReference.catalogItemId` is NOT the actual catalog item ID, this field will return the true item's ID.\n\n+ For example, for Wix Bookings, `catalogReference.catalogItemId` is the booking ID. Therefore this value is set to the service ID.\n+ In most cases, this field is the same as `catalogReference.catalogItemId`.\n+ Used in membership validation."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            depositAmount: z
              .string()
              .describe(
                "Partial payment for the given item to be paid upfront during the checkout.\n\nEligible for catalog items with type `DEPOSIT_ONLINE`.\nWhen omitted, the item's price will not be split and is expected to be paid in a single installment."
              )
              .optional()
              .nullable(),
            deliveryProfileId: z
              .string()
              .describe('Delivery profile ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            catalogReference: z
              .object({
                catalogItemId: z
                  .string()
                  .describe('ID of the item within the catalog it belongs to.')
                  .min(1)
                  .max(36)
                  .optional(),
                appId: z
                  .string()
                  .describe(
                    'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                  )
                  .min(1)
                  .optional(),
                options: z
                  .record(z.string(), z.any())
                  .describe(
                    'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Catalog and item reference. Includes IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
              )
              .optional(),
            priceUndetermined: z
              .boolean()
              .describe(
                'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
              )
              .optional(),
            fixedQuantity: z
              .boolean()
              .describe(
                'Whether the line item quantity is fixed and cannot be changed.\n\nDefault: `false`'
              )
              .optional(),
            consentRequiredPaymentPolicy: z
              .string()
              .describe(
                'Item payment policy that requires customer consent to complete purchase. The payment policy will be displayed on the checkout page.'
              )
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
            savePaymentMethod: z
              .boolean()
              .describe(
                'Whether to save the payment method on the order.\n\nDefault: `false`'
              )
              .optional(),
            policies: z
              .array(
                z.object({
                  title: z
                    .string()
                    .describe('Policy title - should be translated')
                    .min(1)
                    .max(29)
                    .optional()
                    .nullable(),
                  content: z
                    .string()
                    .describe('Policy content - should be translated')
                    .min(1)
                    .max(3000)
                    .optional(),
                })
              )
              .max(5)
              .optional(),
            membersOnly: z
              .boolean()
              .describe(
                'Whether the item can only be purchased by site members.\n\nDefault: `false`'
              )
              .optional(),
            taxableAddress: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ addressType: z.never().optional() }),
                  z.object({
                    addressType: z
                      .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                      .describe(
                        'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                      ),
                  }),
                ])
              )
              .describe('Address to use for tax calculation purposes.')
              .optional(),
            modifierGroups: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Modifier group ID.')
                    .min(1)
                    .max(36)
                    .optional(),
                  name: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe('Modifier group name.')
                    .optional(),
                  modifiers: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Modifier ID.')
                          .min(1)
                          .max(36)
                          .optional(),
                        quantity: z
                          .number()
                          .int()
                          .describe('The quantity of this modifier.')
                          .min(1)
                          .max(100000)
                          .optional()
                          .nullable(),
                        label: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Primary display label for the modifier.')
                          .optional(),
                        details: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Additional details.')
                          .optional(),
                        price: z
                          .string()
                          .describe('The price of the modifier.')
                          .optional()
                          .nullable(),
                      })
                    )
                    .min(1)
                    .max(25)
                    .optional(),
                })
              )
              .max(25)
              .optional(),
          })
        )
        .max(300)
        .optional(),
      couponCode: z
        .string()
        .describe(
          'Coupon code.\n\nNote that a checkout can only hold one `couponCode` at a time. If an additional `couponCode` is added, it will override the existing `couponCode`.\nFor additional information, see the Coupons API.'
        )
        .optional()
        .nullable(),
      customContentReference: z
        .object({
          appId: z
            .string()
            .describe(
              "ID of the app providing the content.\n\nYou can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          componentId: z
            .string()
            .describe(
              "ID of the component within the app it belongs to.\n\nYou can get your component's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          'Reference IDs for the app and component providing custom checkout page content.\n\nTo access and manage custom checkout page content, your app must have the permission scope named "Manage eCommerce - Admin Permissions".\nLearn more about [permission scopes](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/about-permissions).'
        )
        .optional(),
      externalReference: z
        .object({
          appId: z
            .string()
            .describe(
              'ID of the app associated with the purchase flow.\nFor example, the Wix Pay Links app ID.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          resourceId: z
            .string()
            .describe(
              'Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.\nFor example, a Wix Pay Link ID.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
        })
        .describe(
          'References to an external app and resource associated with the checkout.\nUsed for integration and tracking across different platforms.'
        )
        .optional(),
    })
    .describe('Checkout template info to update.'),
});
export const UpdateCheckoutTemplateResponse = z.object({
  _id: z
    .string()
    .describe('Checkout template ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  status: z
    .enum(['ACTIVE', 'INACTIVE'])
    .describe(
      'Status of the checkout template.\n\nWhen `status` is `INACTIVE` checkouts will not be created with this template `id`. Instead, the endpoint will redirect to the domain site.\n\nDefault: `ACTIVE`'
    )
    .optional(),
  customization: z
    .object({
      webClient: z
        .object({
          disabledContinueShoppingButton: z
            .boolean()
            .describe(
              'Whether to disable the "Continue shopping" button.\n\nDefault: `false`'
            )
            .optional(),
          disabledPolicyAgreementCheckbox: z
            .boolean()
            .describe(
              'Whether to disable policy agreement checkout in the checkout page\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe(
          'Web client customizations. These customizations only apply to the standard Wix checkout page.'
        )
        .optional(),
      lockedGiftCard: z
        .boolean()
        .describe(
          'Whether to lock the ability to add or remove a gift card.\n\nDefault: `false`'
        )
        .optional(),
      lockedCoupon: z
        .boolean()
        .describe(
          'Whether to lock the ability to apply or remove a coupon code.\n\nDefault: `false`'
        )
        .optional(),
      disabledManualPayment: z
        .boolean()
        .describe(
          'Whether to disable manual payment option for this checkout.\n\nDefault: `false`'
        )
        .optional(),
    })
    .describe(
      'Custom settings to apply to the checkout page created from this template.'
    )
    .optional(),
  lineItems: z
    .array(
      z.object({
        quantity: z
          .number()
          .int()
          .describe('Item quantity.\n\nMin: `1` <br />\nMax: `100000`')
          .min(1)
          .max(100000)
          .optional(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Catalog and item reference. Holds IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
          )
          .optional(),
        catalogOverrideFields: z
          .object({
            productName: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe('Item name.')
              .optional(),
            price: z
              .string()
              .describe('Item price **after** discounts.')
              .optional()
              .nullable(),
            fullPrice: z
              .string()
              .describe('Item price **before** discounts.')
              .optional()
              .nullable(),
            descriptionLines: z
              .array(
                z.intersection(
                  z.object({
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                          )
                          .max(100)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                      .describe('Description line name.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z.never().optional(),
                    }),
                    z.object({
                      colorInfo: z.never().optional(),
                      plainText: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(600)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(600)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line plain text value.'),
                    }),
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(500)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          code: z
                            .string()
                            .describe('HEX or RGB color code for display.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line color value.'),
                    }),
                  ])
                )
              )
              .min(0)
              .max(20)
              .optional(),
            physicalProperties: z
              .object({
                weight: z
                  .number()
                  .describe(
                    'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe(
                    'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                  )
                  .max(40)
                  .optional()
                  .nullable(),
                shippable: z
                  .boolean()
                  .describe('Whether this line item is shippable.')
                  .optional(),
              })
              .describe('Physical properties of the item.')
              .optional(),
            image: z.string().describe('Item image.').optional(),
            paymentOption: z
              .object({
                value: z
                  .enum([
                    'FULL_PAYMENT_ONLINE',
                    'FULL_PAYMENT_OFFLINE',
                    'MEMBERSHIP',
                    'DEPOSIT_ONLINE',
                    'MEMBERSHIP_OFFLINE',
                    'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                  ])
                  .describe(
                    'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
                  )
                  .optional(),
              })
              .describe('Payment method selected for the item.')
              .optional(),
            depositAmount: z
              .string()
              .describe(
                'Only eligible for catalog items with `lineItem.paymentOption.value` type of `DEPOSIT_ONLINE`.'
              )
              .optional()
              .nullable(),
            savePaymentMethod: z
              .boolean()
              .describe(
                'Whether to save the payment method on the order.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
            inventoryAppId: z
              .string()
              .describe('ID of the app managing the inventory.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe('Overriding values for catalog item properties.')
          .optional(),
      })
    )
    .max(300)
    .optional(),
  customLineItems: z
    .array(
      z.object({
        quantity: z
          .number()
          .int()
          .describe('Custom line item quantity.\n\nMin: `1`\nMax: `100000`')
          .min(1)
          .max(100000)
          .optional(),
        price: z
          .string()
          .describe(
            'Custom line item price.\n\nMust be a number or a decimal without symbols.'
          )
          .optional(),
        priceDescription: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
              )
              .min(1)
              .max(100)
              .optional(),
            translated: z
              .string()
              .describe(
                "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
          })
          .describe(
            'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
          )
          .optional(),
        descriptionLines: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                      )
                      .max(100)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe('Description line name.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z.never().optional(),
                }),
                z.object({
                  colorInfo: z.never().optional(),
                  plainText: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(600)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(600)
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line plain text value.'),
                }),
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(500)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      code: z
                        .string()
                        .describe('HEX or RGB color code for display.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line color value.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        media: z.string().describe("Custom line item's media.").optional(),
        _id: z
          .string()
          .describe(
            'Deprecated. Custom line item ID.\n\nTo maintain a unique value for a custom line item across multiple carts and orders, pass `catalogReference` instead.\nLearn more about [custom items with catalog reference](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/catalogs/catalog-and-custom-items#custom-item-with-catalog-reference).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        taxGroupId: z
          .string()
          .describe('Tax group ID for this custom line item.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        productName: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
              )
              .min(1)
              .max(200)
              .optional(),
            translated: z
              .string()
              .describe(
                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
              )
              .min(1)
              .max(400)
              .optional()
              .nullable(),
          })
          .describe('Name of the item or product.')
          .optional(),
        url: z
          .string()
          .describe(
            "URL to the item's page on the site. When not provided, the link back from the cart page to the relevant product page will not work."
          )
          .optional(),
        itemType: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                preset: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                preset: z
                  .enum([
                    'UNRECOGNISED',
                    'PHYSICAL',
                    'DIGITAL',
                    'GIFT_CARD',
                    'SERVICE',
                  ])
                  .describe('Preset item type.'),
              }),
              z.object({
                preset: z.never().optional(),
                custom: z
                  .string()
                  .describe(
                    'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                  ),
              }),
            ])
          )
          .describe('Item type. Either a preset type or custom.')
          .optional(),
        fullPrice: z
          .string()
          .describe(
            'Item price **before** catalog-defined discount. Defaults to `price` when not provided.'
          )
          .optional()
          .nullable(),
        quantityAvailable: z
          .number()
          .int()
          .describe(
            'Item quantity available for purchase. Only return this if inventory is managed.\nNot returning this field means that the buyer can "infinitely" tick up the number of items in the cart.'
          )
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        physicalProperties: z
          .object({
            weight: z
              .number()
              .describe(
                'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
              )
              .optional()
              .nullable(),
            sku: z
              .string()
              .describe(
                'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
              )
              .max(40)
              .optional()
              .nullable(),
            shippable: z
              .boolean()
              .describe('Whether this line item is shippable.')
              .optional(),
          })
          .describe('Physical properties of the item.')
          .optional(),
        paymentOption: z
          .enum([
            'FULL_PAYMENT_ONLINE',
            'FULL_PAYMENT_OFFLINE',
            'MEMBERSHIP',
            'DEPOSIT_ONLINE',
            'MEMBERSHIP_OFFLINE',
            'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
          ])
          .describe(
            'Type of selected payment option for current item. Defaults to `FULL_PAYMENT_ONLINE`.\n\n+ `FULL_PAYMENT_ONLINE` - Entire payment for this item happens as part of the checkout.\n+ `FULL_PAYMENT_OFFLINE` - Entire payment for this item happens after the checkout. For example, when using cash, check, or other offline payment methods.\n+ `MEMBERSHIP` - Payment for this item is done by charging a membership. When this option is used, `lineItem.price.amount` is 0.\n+ `DEPOSIT_ONLINE` -  Partial payment for the given item to be paid upfront during the checkout. Amount to be paid is defined by deposit_amount field.'
          )
          .optional(),
        serviceProperties: z
          .object({
            scheduledDate: z
              .date()
              .describe(
                'Date and time the service is to be provided, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nFor example, the start time of a class.'
              )
              .optional()
              .nullable(),
            numberOfParticipants: z
              .number()
              .int()
              .describe(
                'The number of people participating in the service. For example, the number of people attending a class or the number of people per hotel room.'
              )
              .min(1)
              .max(10000)
              .optional()
              .nullable(),
          })
          .describe(
            'Service properties. When relevant, this contains information such as date and number of participants.\nUsed, among other things, when checking for valid memberships.'
          )
          .optional(),
        rootCatalogItemId: z
          .string()
          .describe(
            "In cases where `catalogReference.catalogItemId` is NOT the actual catalog item ID, this field will return the true item's ID.\n\n+ For example, for Wix Bookings, `catalogReference.catalogItemId` is the booking ID. Therefore this value is set to the service ID.\n+ In most cases, this field is the same as `catalogReference.catalogItemId`.\n+ Used in membership validation."
          )
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        depositAmount: z
          .string()
          .describe(
            "Partial payment for the given item to be paid upfront during the checkout.\n\nEligible for catalog items with type `DEPOSIT_ONLINE`.\nWhen omitted, the item's price will not be split and is expected to be paid in a single installment."
          )
          .optional()
          .nullable(),
        deliveryProfileId: z
          .string()
          .describe('Delivery profile ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Catalog and item reference. Includes IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
          )
          .optional(),
        priceUndetermined: z
          .boolean()
          .describe(
            'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
          )
          .optional(),
        fixedQuantity: z
          .boolean()
          .describe(
            'Whether the line item quantity is fixed and cannot be changed.\n\nDefault: `false`'
          )
          .optional(),
        consentRequiredPaymentPolicy: z
          .string()
          .describe(
            'Item payment policy that requires customer consent to complete purchase. The payment policy will be displayed on the checkout page.'
          )
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
        savePaymentMethod: z
          .boolean()
          .describe(
            'Whether to save the payment method on the order.\n\nDefault: `false`'
          )
          .optional(),
        policies: z
          .array(
            z.object({
              title: z
                .string()
                .describe('Policy title - should be translated')
                .min(1)
                .max(29)
                .optional()
                .nullable(),
              content: z
                .string()
                .describe('Policy content - should be translated')
                .min(1)
                .max(3000)
                .optional(),
            })
          )
          .max(5)
          .optional(),
        membersOnly: z
          .boolean()
          .describe(
            'Whether the item can only be purchased by site members.\n\nDefault: `false`'
          )
          .optional(),
        taxableAddress: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ addressType: z.never().optional() }),
              z.object({
                addressType: z
                  .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                  .describe(
                    'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                  ),
              }),
            ])
          )
          .describe('Address to use for tax calculation purposes.')
          .optional(),
        modifierGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Modifier group ID.')
                .min(1)
                .max(36)
                .optional(),
              name: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe('Modifier group name.')
                .optional(),
              modifiers: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe('The quantity of this modifier.')
                      .min(1)
                      .max(100000)
                      .optional()
                      .nullable(),
                    label: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Primary display label for the modifier.')
                      .optional(),
                    details: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Additional details.')
                      .optional(),
                    price: z
                      .string()
                      .describe('The price of the modifier.')
                      .optional()
                      .nullable(),
                  })
                )
                .min(1)
                .max(25)
                .optional(),
            })
          )
          .max(25)
          .optional(),
      })
    )
    .max(300)
    .optional(),
  couponCode: z
    .string()
    .describe(
      'Coupon code.\n\nNote that a checkout can only hold one `couponCode` at a time. If an additional `couponCode` is added, it will override the existing `couponCode`.\nFor additional information, see the Coupons API.'
    )
    .optional()
    .nullable(),
  customContentReference: z
    .object({
      appId: z
        .string()
        .describe(
          "ID of the app providing the content.\n\nYou can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      componentId: z
        .string()
        .describe(
          "ID of the component within the app it belongs to.\n\nYou can get your component's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
    })
    .describe(
      'Reference IDs for the app and component providing custom checkout page content.\n\nTo access and manage custom checkout page content, your app must have the permission scope named "Manage eCommerce - Admin Permissions".\nLearn more about [permission scopes](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/about-permissions).'
    )
    .optional(),
  externalReference: z
    .object({
      appId: z
        .string()
        .describe(
          'ID of the app associated with the purchase flow.\nFor example, the Wix Pay Links app ID.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      resourceId: z
        .string()
        .describe(
          'Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.\nFor example, a Wix Pay Link ID.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
    })
    .describe(
      'References to an external app and resource associated with the checkout.\nUsed for integration and tracking across different platforms.'
    )
    .optional(),
});
export const DeleteCheckoutTemplateRequest = z.object({
  checkoutTemplateId: z
    .string()
    .describe('ID of the checkout template to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteCheckoutTemplateResponse = z.object({});
export const QueryCheckoutTemplatesRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          status: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z.enum(['_id', 'status']).optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryCheckoutTemplatesResponse = z.object({
  checkoutTemplates: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Checkout template ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        status: z
          .enum(['ACTIVE', 'INACTIVE'])
          .describe(
            'Status of the checkout template.\n\nWhen `status` is `INACTIVE` checkouts will not be created with this template `id`. Instead, the endpoint will redirect to the domain site.\n\nDefault: `ACTIVE`'
          )
          .optional(),
        customization: z
          .object({
            webClient: z
              .object({
                disabledContinueShoppingButton: z
                  .boolean()
                  .describe(
                    'Whether to disable the "Continue shopping" button.\n\nDefault: `false`'
                  )
                  .optional(),
                disabledPolicyAgreementCheckbox: z
                  .boolean()
                  .describe(
                    'Whether to disable policy agreement checkout in the checkout page\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe(
                'Web client customizations. These customizations only apply to the standard Wix checkout page.'
              )
              .optional(),
            lockedGiftCard: z
              .boolean()
              .describe(
                'Whether to lock the ability to add or remove a gift card.\n\nDefault: `false`'
              )
              .optional(),
            lockedCoupon: z
              .boolean()
              .describe(
                'Whether to lock the ability to apply or remove a coupon code.\n\nDefault: `false`'
              )
              .optional(),
            disabledManualPayment: z
              .boolean()
              .describe(
                'Whether to disable manual payment option for this checkout.\n\nDefault: `false`'
              )
              .optional(),
          })
          .describe(
            'Custom settings to apply to the checkout page created from this template.'
          )
          .optional(),
        lineItems: z
          .array(
            z.object({
              quantity: z
                .number()
                .int()
                .describe('Item quantity.\n\nMin: `1` <br />\nMax: `100000`')
                .min(1)
                .max(100000)
                .optional(),
              catalogReference: z
                .object({
                  catalogItemId: z
                    .string()
                    .describe(
                      'ID of the item within the catalog it belongs to.'
                    )
                    .min(1)
                    .max(36)
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                    )
                    .min(1)
                    .optional(),
                  options: z
                    .record(z.string(), z.any())
                    .describe(
                      'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Catalog and item reference. Holds IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
                )
                .optional(),
              catalogOverrideFields: z
                .object({
                  productName: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe('Item name.')
                    .optional(),
                  price: z
                    .string()
                    .describe('Item price **after** discounts.')
                    .optional()
                    .nullable(),
                  fullPrice: z
                    .string()
                    .describe('Item price **before** discounts.')
                    .optional()
                    .nullable(),
                  descriptionLines: z
                    .array(
                      z.intersection(
                        z.object({
                          name: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                )
                                .max(100)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                                )
                                .max(200)
                                .optional()
                                .nullable(),
                            })
                            .describe('Description line name.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            plainText: z.never().optional(),
                            colorInfo: z.never().optional(),
                          }),
                          z.object({
                            colorInfo: z.never().optional(),
                            plainText: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                  )
                                  .max(600)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                                  )
                                  .max(600)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Description line plain text value.'),
                          }),
                          z.object({
                            plainText: z.never().optional(),
                            colorInfo: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                  )
                                  .max(500)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                                  )
                                  .max(500)
                                  .optional()
                                  .nullable(),
                                code: z
                                  .string()
                                  .describe(
                                    'HEX or RGB color code for display.'
                                  )
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Description line color value.'),
                          }),
                        ])
                      )
                    )
                    .min(0)
                    .max(20)
                    .optional(),
                  physicalProperties: z
                    .object({
                      weight: z
                        .number()
                        .describe(
                          'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                        )
                        .optional()
                        .nullable(),
                      sku: z
                        .string()
                        .describe(
                          'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                        )
                        .max(40)
                        .optional()
                        .nullable(),
                      shippable: z
                        .boolean()
                        .describe('Whether this line item is shippable.')
                        .optional(),
                    })
                    .describe('Physical properties of the item.')
                    .optional(),
                  image: z.string().describe('Item image.').optional(),
                  paymentOption: z
                    .object({
                      value: z
                        .enum([
                          'FULL_PAYMENT_ONLINE',
                          'FULL_PAYMENT_OFFLINE',
                          'MEMBERSHIP',
                          'DEPOSIT_ONLINE',
                          'MEMBERSHIP_OFFLINE',
                          'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                        ])
                        .describe(
                          'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
                        )
                        .optional(),
                    })
                    .describe('Payment method selected for the item.')
                    .optional(),
                  depositAmount: z
                    .string()
                    .describe(
                      'Only eligible for catalog items with `lineItem.paymentOption.value` type of `DEPOSIT_ONLINE`.'
                    )
                    .optional()
                    .nullable(),
                  savePaymentMethod: z
                    .boolean()
                    .describe(
                      'Whether to save the payment method on the order.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                  inventoryAppId: z
                    .string()
                    .describe('ID of the app managing the inventory.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Overriding values for catalog item properties.')
                .optional(),
            })
          )
          .max(300)
          .optional(),
        customLineItems: z
          .array(
            z.object({
              quantity: z
                .number()
                .int()
                .describe(
                  'Custom line item quantity.\n\nMin: `1`\nMax: `100000`'
                )
                .min(1)
                .max(100000)
                .optional(),
              price: z
                .string()
                .describe(
                  'Custom line item price.\n\nMust be a number or a decimal without symbols.'
                )
                .optional(),
              priceDescription: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                    )
                    .min(1)
                    .max(100)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
                )
                .optional(),
              descriptionLines: z
                .array(
                  z.intersection(
                    z.object({
                      name: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(100)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line name.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        plainText: z.never().optional(),
                        colorInfo: z.never().optional(),
                      }),
                      z.object({
                        colorInfo: z.never().optional(),
                        plainText: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                              )
                              .max(600)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                              )
                              .max(600)
                              .optional()
                              .nullable(),
                          })
                          .describe('Description line plain text value.'),
                      }),
                      z.object({
                        plainText: z.never().optional(),
                        colorInfo: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                              )
                              .max(500)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                              )
                              .max(500)
                              .optional()
                              .nullable(),
                            code: z
                              .string()
                              .describe('HEX or RGB color code for display.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Description line color value.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
              media: z
                .string()
                .describe("Custom line item's media.")
                .optional(),
              _id: z
                .string()
                .describe(
                  'Deprecated. Custom line item ID.\n\nTo maintain a unique value for a custom line item across multiple carts and orders, pass `catalogReference` instead.\nLearn more about [custom items with catalog reference](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/catalogs/catalog-and-custom-items#custom-item-with-catalog-reference).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              taxGroupId: z
                .string()
                .describe('Tax group ID for this custom line item.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              productName: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe('Name of the item or product.')
                .optional(),
              url: z
                .string()
                .describe(
                  "URL to the item's page on the site. When not provided, the link back from the cart page to the relevant product page will not work."
                )
                .optional(),
              itemType: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      preset: z.never().optional(),
                      custom: z.never().optional(),
                    }),
                    z.object({
                      custom: z.never().optional(),
                      preset: z
                        .enum([
                          'UNRECOGNISED',
                          'PHYSICAL',
                          'DIGITAL',
                          'GIFT_CARD',
                          'SERVICE',
                        ])
                        .describe('Preset item type.'),
                    }),
                    z.object({
                      preset: z.never().optional(),
                      custom: z
                        .string()
                        .describe(
                          'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                        ),
                    }),
                  ])
                )
                .describe('Item type. Either a preset type or custom.')
                .optional(),
              fullPrice: z
                .string()
                .describe(
                  'Item price **before** catalog-defined discount. Defaults to `price` when not provided.'
                )
                .optional()
                .nullable(),
              quantityAvailable: z
                .number()
                .int()
                .describe(
                  'Item quantity available for purchase. Only return this if inventory is managed.\nNot returning this field means that the buyer can "infinitely" tick up the number of items in the cart.'
                )
                .min(0)
                .max(100000)
                .optional()
                .nullable(),
              physicalProperties: z
                .object({
                  weight: z
                    .number()
                    .describe(
                      'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                    )
                    .optional()
                    .nullable(),
                  sku: z
                    .string()
                    .describe(
                      'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                    )
                    .max(40)
                    .optional()
                    .nullable(),
                  shippable: z
                    .boolean()
                    .describe('Whether this line item is shippable.')
                    .optional(),
                })
                .describe('Physical properties of the item.')
                .optional(),
              paymentOption: z
                .enum([
                  'FULL_PAYMENT_ONLINE',
                  'FULL_PAYMENT_OFFLINE',
                  'MEMBERSHIP',
                  'DEPOSIT_ONLINE',
                  'MEMBERSHIP_OFFLINE',
                  'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                ])
                .describe(
                  'Type of selected payment option for current item. Defaults to `FULL_PAYMENT_ONLINE`.\n\n+ `FULL_PAYMENT_ONLINE` - Entire payment for this item happens as part of the checkout.\n+ `FULL_PAYMENT_OFFLINE` - Entire payment for this item happens after the checkout. For example, when using cash, check, or other offline payment methods.\n+ `MEMBERSHIP` - Payment for this item is done by charging a membership. When this option is used, `lineItem.price.amount` is 0.\n+ `DEPOSIT_ONLINE` -  Partial payment for the given item to be paid upfront during the checkout. Amount to be paid is defined by deposit_amount field.'
                )
                .optional(),
              serviceProperties: z
                .object({
                  scheduledDate: z
                    .date()
                    .describe(
                      'Date and time the service is to be provided, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nFor example, the start time of a class.'
                    )
                    .optional()
                    .nullable(),
                  numberOfParticipants: z
                    .number()
                    .int()
                    .describe(
                      'The number of people participating in the service. For example, the number of people attending a class or the number of people per hotel room.'
                    )
                    .min(1)
                    .max(10000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Service properties. When relevant, this contains information such as date and number of participants.\nUsed, among other things, when checking for valid memberships.'
                )
                .optional(),
              rootCatalogItemId: z
                .string()
                .describe(
                  "In cases where `catalogReference.catalogItemId` is NOT the actual catalog item ID, this field will return the true item's ID.\n\n+ For example, for Wix Bookings, `catalogReference.catalogItemId` is the booking ID. Therefore this value is set to the service ID.\n+ In most cases, this field is the same as `catalogReference.catalogItemId`.\n+ Used in membership validation."
                )
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              depositAmount: z
                .string()
                .describe(
                  "Partial payment for the given item to be paid upfront during the checkout.\n\nEligible for catalog items with type `DEPOSIT_ONLINE`.\nWhen omitted, the item's price will not be split and is expected to be paid in a single installment."
                )
                .optional()
                .nullable(),
              deliveryProfileId: z
                .string()
                .describe('Delivery profile ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              catalogReference: z
                .object({
                  catalogItemId: z
                    .string()
                    .describe(
                      'ID of the item within the catalog it belongs to.'
                    )
                    .min(1)
                    .max(36)
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                    )
                    .min(1)
                    .optional(),
                  options: z
                    .record(z.string(), z.any())
                    .describe(
                      'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Catalog and item reference. Includes IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin."
                )
                .optional(),
              priceUndetermined: z
                .boolean()
                .describe(
                  'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
                )
                .optional(),
              fixedQuantity: z
                .boolean()
                .describe(
                  'Whether the line item quantity is fixed and cannot be changed.\n\nDefault: `false`'
                )
                .optional(),
              consentRequiredPaymentPolicy: z
                .string()
                .describe(
                  'Item payment policy that requires customer consent to complete purchase. The payment policy will be displayed on the checkout page.'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              savePaymentMethod: z
                .boolean()
                .describe(
                  'Whether to save the payment method on the order.\n\nDefault: `false`'
                )
                .optional(),
              policies: z
                .array(
                  z.object({
                    title: z
                      .string()
                      .describe('Policy title - should be translated')
                      .min(1)
                      .max(29)
                      .optional()
                      .nullable(),
                    content: z
                      .string()
                      .describe('Policy content - should be translated')
                      .min(1)
                      .max(3000)
                      .optional(),
                  })
                )
                .max(5)
                .optional(),
              membersOnly: z
                .boolean()
                .describe(
                  'Whether the item can only be purchased by site members.\n\nDefault: `false`'
                )
                .optional(),
              taxableAddress: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({ addressType: z.never().optional() }),
                    z.object({
                      addressType: z
                        .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                        .describe(
                          'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                        ),
                    }),
                  ])
                )
                .describe('Address to use for tax calculation purposes.')
                .optional(),
              modifierGroups: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier group ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Modifier group name.')
                      .optional(),
                    modifiers: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Modifier ID.')
                            .min(1)
                            .max(36)
                            .optional(),
                          quantity: z
                            .number()
                            .int()
                            .describe('The quantity of this modifier.')
                            .min(1)
                            .max(100000)
                            .optional()
                            .nullable(),
                          label: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                )
                                .min(1)
                                .max(200)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                )
                                .min(1)
                                .max(400)
                                .optional()
                                .nullable(),
                            })
                            .describe('Primary display label for the modifier.')
                            .optional(),
                          details: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                )
                                .min(1)
                                .max(200)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                )
                                .min(1)
                                .max(400)
                                .optional()
                                .nullable(),
                            })
                            .describe('Additional details.')
                            .optional(),
                          price: z
                            .string()
                            .describe('The price of the modifier.')
                            .optional()
                            .nullable(),
                        })
                      )
                      .min(1)
                      .max(25)
                      .optional(),
                  })
                )
                .max(25)
                .optional(),
            })
          )
          .max(300)
          .optional(),
        couponCode: z
          .string()
          .describe(
            'Coupon code.\n\nNote that a checkout can only hold one `couponCode` at a time. If an additional `couponCode` is added, it will override the existing `couponCode`.\nFor additional information, see the Coupons API.'
          )
          .optional()
          .nullable(),
        customContentReference: z
          .object({
            appId: z
              .string()
              .describe(
                "ID of the app providing the content.\n\nYou can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            componentId: z
              .string()
              .describe(
                "ID of the component within the app it belongs to.\n\nYou can get your component's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe(
            'Reference IDs for the app and component providing custom checkout page content.\n\nTo access and manage custom checkout page content, your app must have the permission scope named "Manage eCommerce - Admin Permissions".\nLearn more about [permission scopes](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/about-permissions).'
          )
          .optional(),
        externalReference: z
          .object({
            appId: z
              .string()
              .describe(
                'ID of the app associated with the purchase flow.\nFor example, the Wix Pay Links app ID.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            resourceId: z
              .string()
              .describe(
                'Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.\nFor example, a Wix Pay Link ID.'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
          })
          .describe(
            'References to an external app and resource associated with the checkout.\nUsed for integration and tracking across different platforms.'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const CreateCheckoutFromTemplateRequest = z.object({
  checkoutTemplateId: z
    .string()
    .describe('ID of the checkout template to use to create a checkout from.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  siteId: z
    .string()
    .describe('ID of the site associated with the checkout template.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const CreateCheckoutFromTemplateResponse = z.object({
  checkoutId: z
    .string()
    .describe('ID of the created checkout.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  checkoutUrl: z
    .string()
    .describe('URL of the created checkout page.')
    .optional(),
  purchaseFlowId: z
    .string()
    .describe(
      'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const CreateAndRedirectToCheckoutRequest = z.object({
  checkoutTemplateId: z
    .string()
    .describe('ID of the checkout template to use to create a checkout.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  siteId: z
    .string()
    .describe('ID of the site associated with the checkout template.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const CreateAndRedirectToCheckoutResponse = z.object({
  body: z.string().optional(),
  statusCode: z.number().int().optional().nullable(),
  headers: z
    .array(
      z.object({ key: z.string().optional(), value: z.string().optional() })
    )
    .optional(),
});
